import styles from './sales-offer-dialogs.module.scss';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, PrimaryButton, GrayOutlinedButton, Typography, CreateHeaders, textHeader, HeaderSizeEnum, Icon, SecondaryButton, getLicenseTypeStringToString } from '@frontend-workspace/react-shared-lib';
import WebkuaTable from '../../webkua-table/webkua-table';
import { getPlainDateWithoutHooks } from '../../helpers';
import { IOfferContractsWithProductSeats } from '../../_models/offer-contracts-with-product';
import { ProposalStateEnum } from '../../_enums/proposal-state';
import { ValueFormatterParams } from 'ag-grid-community';

/* eslint-disable-next-line */
export interface SalesOfferDialogsProps {
  open: boolean,
  onClose: (event: CloseEvent) => void
}

export interface ShopDialogsProps {
  open: boolean,
  onClose: (event: CloseEvent) => void
}

export interface ContractDetailsDialogProps extends ShopDialogsProps {
  contract: IOfferContractsWithProductSeats,
  onConfirm?: (...args: any[]) => void,
  onCancel?: (...args: any[]) => void,
}

export function ContractDetailsDialog(props: ContractDetailsDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth='xl'
      fullWidth={true}>
      <DialogTitle className='mb-4'>
        {t("shop.contractDetails")}
      </DialogTitle>
      <DialogContent>
        <div className='mb-1'>
          <Typography variant='body4' component='span' className="me-1">
            {t("shop.contractNumber")}
          </Typography>
          <Typography variant='body6' component='span'>
            {props.contract.contractNumber}
          </Typography>
        </div>
        <div className='mb-1'>
          <Typography
            variant='body4' component='span' className="me-1">
            {t("shop.renewalDate")}:
          </Typography>
          <Typography variant='body6' component='span'>
            {getPlainDateWithoutHooks(new Date(props.contract.renewalDate))}
          </Typography>
        </div>
        <div className='mb-3 mb-lg-1'>
          <Typography
            variant='body4' component='span' className="me-1">
            {t("shop.contractResponsible")}:
          </Typography>
          <Typography variant='body6' component='span'>
            {props.contract.contractResponsibleName}
          </Typography>
        </div>
        <WebkuaTable
          headers={CreateHeaders([
            textHeader("licenseName", t("standard.licenses"), HeaderSizeEnum.EXTRA_LARGE),
            textHeader("quantity", t("standard.seats"), HeaderSizeEnum.MEDIUM, {
              cellStyle: { justifyContent: 'right' },
              headerClass: "ag-right-aligned-header"
            }),
            textHeader("licenseType", t("standard.licenseType"), HeaderSizeEnum.MEDIUM, {
              valueFormatter: (params: ValueFormatterParams) => {
                return t(getLicenseTypeStringToString(params))
              }
            }),
            textHeader("licenseNumber", t("standard.licenseNr"), HeaderSizeEnum.MEDIUM,)
          ])}
          data={props.contract.licenses}
          pageSize={10}
        />
      </DialogContent>
      <DialogActions>
        {props.onConfirm &&
          <PrimaryButton
            variant="contained"
            size='large'
            onClick={props.onConfirm}
            className='minButtonWidth'>
            <Icon className="me-1" fontSize="small">add</Icon>{t("Legg til")}
          </PrimaryButton>
        }
        {props.onCancel &&
          <GrayOutlinedButton
            variant="outlined"
            size='large'
            onClick={props.onCancel}
            className='minButtonWidth'>
            {t("standard.back")}
          </GrayOutlinedButton>
        }
        {!props.onCancel &&
          <GrayOutlinedButton
            variant="outlined"
            size='large'
            onClick={props.onClose}
            className='minButtonWidth'>
            {t("standard.close")}
          </GrayOutlinedButton>
        }
      </DialogActions>
    </Dialog>
  );
}

export interface SubmitOfferWarningDialogProps {
  open: boolean,
  state: ProposalStateEnum,
  hasError: boolean,
  onSubmitOffer: (event: any) => void,
  onClose: (event: any, reason: any) => void,
}
export const SubmitOfferWarningDialog = (props: SubmitOfferWarningDialogProps) => {
  const { t } = useTranslation();

  return <Dialog
    open={props.open}
    onClose={props.onClose}
    maxWidth='sm'>
    <DialogTitle className='mb-3'>
      {t("shop.confirmOfferTitle")}
    </DialogTitle>
    <DialogContent>
      {props.hasError ?
        <>
          <Typography variant="body2" component="div">
            {t(`shop.offerHasErrorText`)}
          </Typography>
          <Typography variant="body2" component="div">
            {t(`shop.offerHasErrorText2`)}
          </Typography>
        </> :
        <>
          <Typography variant="body2" component="div">
            {t("shop.confirmOfferText")}
          </Typography>
          <Typography variant="body2" component="div">
            {t(`shop.confirmOfferText${props.state}`)}
          </Typography>
        </>
      }
    </DialogContent>
    <DialogActions>
      <PrimaryButton
        variant="contained"
        size='large'
        disabled={props.hasError}
        onClick={props.onSubmitOffer}
        className='minButtonWidth'>
        {t("shop.sendOffer")}
      </PrimaryButton>
      <GrayOutlinedButton
        variant="outlined"
        size='large'
        onClick={props.onClose}
        className='minButtonWidth'>
        {t("standard.cancel")}
      </GrayOutlinedButton>
    </DialogActions>
  </Dialog>
}